import React, { useState, useEffect, useRef } from 'react';
import { Ticket, Gift, Send, Bot, User } from 'lucide-react';
import Popup from '../components/Popup';
import { apiUrl } from '../utils/apiUrl';
import './Pavilion.css';

function Pavilion() {
  const [hasVoucher, setHasVoucher] = useState(false);
  const [voucherRedeemed, setVoucherRedeemed] = useState(false);
  const [hasBadge, setHasBadge] = useState(false);
  const [giftRedeemed, setGiftRedeemed] = useState(false);
  const [popup, setPopup] = useState({ isOpen: false, title: '', message: '' });
  
  const [chatMessages, setChatMessages] = useState([
    { sender: 'bot', text: 'Hi there! I am your Durian AI Assistant. Ask me anything about our durians!' }
  ]);
  const [inputText, setInputText] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const chatEndRef = useRef(null);

  useEffect(() => {
    const voucher = localStorage.getItem('durian_voucher_available');
    const vRedeemed = localStorage.getItem('durian_voucher_redeemed');
    const badge = localStorage.getItem('durian_badge_earned');
    const gRedeemed = localStorage.getItem('durian_gift_redeemed');
    
    if (voucher === 'true' && !vRedeemed) setHasVoucher(true);
    if (vRedeemed === 'true') setVoucherRedeemed(true);
    if (badge === 'true') setHasBadge(true);
    if (gRedeemed === 'true') setGiftRedeemed(true);
  }, []);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages, isTyping]);

  const handleRedeemVoucher = () => {
    // Simulate swipe/redeem
    localStorage.setItem('durian_voucher_redeemed', 'true');
    setHasVoucher(false);
    setVoucherRedeemed(true);
    setPopup({ 
      isOpen: true, 
      title: 'Redeemed!', 
      message: 'Voucher redeemed successfully! Enjoy your Durian Cendol.',
      icon: <img src="/durian-cendol.png" alt="Cendol" style={{ width: '100px', height: '100px', objectFit: 'contain', filter: 'drop-shadow(0 8px 16px rgba(0,0,0,0.15))' }} />
    });
  };

  const handleRedeemGift = () => {
    localStorage.setItem('durian_gift_redeemed', 'true');
    setGiftRedeemed(true);
    setPopup({ 
      isOpen: true, 
      title: 'Gift Unlocked!', 
      message: 'Mystery Gift redeemed! You received a Premium Durian Tasting Set.',
      icon: <img src="/mystery-gift.png" alt="Gift" style={{ width: '100px', height: '100px', objectFit: 'contain', filter: 'drop-shadow(0 8px 16px rgba(0,0,0,0.15))' }} />
    });
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!inputText.trim()) return;

    const userMsg = inputText.trim();
    setChatMessages(prev => [...prev, { sender: 'user', text: userMsg }]);
    setInputText('');
    setIsTyping(true);

    try {
      const response = await fetch(apiUrl('/api/durian/chat'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: userMsg })
      });
      const data = await response.json();
      
      setChatMessages(prev => [...prev, { sender: 'bot', text: data.answer }]);
    } catch (error) {
      // Fallback if backend is down
      setTimeout(() => {
        setChatMessages(prev => [...prev, { 
          sender: 'bot', 
          text: "I'm having trouble connecting to the server. Please ask our staff directly!" 
        }]);
      }, 2000);
    } finally {
      setIsTyping(false);
    }
  };

  return (
    <div className="page-container pavilion-page">
      <div className="header">
        <h2>The Tasting Pavilion</h2>
        <p>Redeem your rewards and learn from our AI.</p>
      </div>

      <div className="rewards-section">
        {hasVoucher && (
          <div className="card reward-card voucher-card">
            <div className="reward-info">
              <img src="/durian-cendol.png" alt="Durian Cendol" className="reward-image" />
              <div>
                <h3>Free Durian Cendol</h3>
                <p>Welcome Gift</p>
              </div>
            </div>
            <button className="btn-primary small-btn" onClick={handleRedeemVoucher}>
              Redeem
            </button>
          </div>
        )}
        
        {voucherRedeemed && (
          <div className="card reward-card redeemed-card">
            <div className="reward-info">
              <img src="/durian-cendol.png" alt="Durian Cendol" className="reward-image grayscale" />
              <div>
                <h3>Durian Cendol</h3>
                <p className="redeemed-text">Redeemed</p>
              </div>
            </div>
            <div className="stamp">CLAIMED</div>
          </div>
        )}

        {hasBadge && !giftRedeemed && (
          <div className="card reward-card gift-card">
            <div className="reward-info">
              <img src="/mystery-gift.png" alt="Mystery Gift" className="reward-image" />
              <div>
                <h3>Mystery Gift</h3>
                <p>For Explorer Badge</p>
              </div>
            </div>
            <button className="btn-primary small-btn" onClick={handleRedeemGift}>
              Open
            </button>
          </div>
        )}

        {giftRedeemed && (
          <div className="card reward-card redeemed-card">
            <div className="reward-info">
              <img src="/mystery-gift.png" alt="Mystery Gift" className="reward-image grayscale" />
              <div>
                <h3>Mystery Gift</h3>
                <p className="redeemed-text">Redeemed</p>
              </div>
            </div>
            <div className="stamp">OPENED</div>
          </div>
        )}
      </div>

      <div className="chat-section">
        <div className="chat-header">
          <div className="chat-header-info">
            <div className="bot-avatar-bg">
              <Bot size={24} color="var(--durian-green-dark)" />
            </div>
            <div>
              <h3>Durian AI</h3>
              <span className="online-status">● Online</span>
            </div>
          </div>
        </div>
        
        <div className="chat-messages">
          {chatMessages.map((msg, index) => (
            <div key={index} className={`message ${msg.sender}`}>
              <div className="message-avatar">
                {msg.sender === 'bot' ? <Bot size={16} /> : <User size={16} />}
              </div>
              <div className="message-bubble">
                {msg.text}
              </div>
            </div>
          ))}
          {isTyping && (
            <div className="message bot">
              <div className="message-avatar"><Bot size={16} /></div>
              <div className="message-bubble typing-indicator">
                <span>.</span><span>.</span><span>.</span>
              </div>
            </div>
          )}
          <div ref={chatEndRef} />
        </div>

        <form className="chat-input-area" onSubmit={handleSendMessage}>
          <input 
            type="text" 
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            placeholder="Ask about our durians..."
          />
          <button type="submit" disabled={!inputText.trim() || isTyping}>
            <Send size={18} />
          </button>
        </form>
      </div>

      <Popup 
        isOpen={popup.isOpen} 
        onClose={() => setPopup({ ...popup, isOpen: false })} 
        title={popup.title} 
        message={popup.message} 
        icon={popup.icon}
      />
    </div>
  );
}

export default Pavilion;
